import { filterBy } from '@progress/kendo-data-query';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import countriesList from '../Shared/CountriesList/CountriesList.json';

function BillingInformationForm({ onChange, showErrors = false, initialValue = {} }) {

    const [state, setState] = useState({
        firstName: "",
        lastName: "",
        billingAddress: "",
        state: "",
        city: "",
        country: {},
        zipCode: ""
    })

    const [focusedFields, setFocusedFields] = useState({
        firstName: false,
        lastName: false,
        billingAddress: false,
        state: false,
        city: false,
        country: false,
        zipCode: false
    })

    const [countries, setCountries] = useState(countriesList);
    const [states, setStates] = useState([]);

    useEffect(() => {
        if (initialValue && Object.keys(initialValue).length > 0) {
            const country = countriesList.find(country => country.code === initialValue.country);
            const state = country.states.find(state => state.code === initialValue.state);
            const { line1: billingAddress, postal_code: zipCode , city} = initialValue;
            const newState = {
                billingAddress,
                country,
                state,
                zipCode,
                city
            };
            if (initialValue.name) {
                const [firstName, ...lastName] = initialValue.name.split(" ");
                newState.firstName = firstName;
                newState.lastName = lastName.join(" ");
            }
            setState(prevState => ({ ...prevState, ...newState }));
        }
    }, [])

    useEffect(() => {
        onChange(getAddressDetails());
    }, [state])

    function getAddressDetails() {
        return {
            name: state.firstName + " " + state.lastName,
            line1: state.billingAddress,
            state: state.state && state.state.code ? state.state.code : "",
            city: state.city,
            country: state.country && state.country.code ? state.country.code : "",
            postal_code: state.zipCode
        }
    }

    function handleChange(e) {
        const { value, name } = e.target;
        setState(prevState => ({
            ...prevState,
            [name]: value === null ? {} : value
        }))
    }

    function handleCountryChange(e) {
        const { value } = e.target;
        setState(prevState => ({
            ...prevState,
            country: value === null ? {} : value,
            state: {}
        }))
        setCountries(countriesList)
        setStates(value ? value.states : []);
    }

    function handleBlur(e) {
        const { name } = e.target;
        setFocusedFields(prevFields => ({
            ...prevFields,
            [name]: showErrors
        }))
    }

    function filterByName(event) {
        const data = event.target.name === "country" ? countriesList : state.country.states;
        const setData = event.target.name === "country" ? setCountries : setStates;
        const slicedData = data.slice();
        setData(filterBy(slicedData, event.filter));
    }
    
    return (
        <>
            <div className='col-12 mt-4'>
                <h5>Billing Information</h5>
            </div>
            <div className='col-6'>
                <label
                    htmlFor="first-name-field"
                    className='form-label'>
                    First Name
                </label>
                <input
                    id="first-name-field"
                    name="firstName"
                    className='form-control'
                    value={state.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.firstName &&
                        !state.firstName?.trim() &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className='col-6'>
                <label
                    htmlFor="last-name-field"
                    className='form-label'>
                    Last Name
                </label>
                <input
                    id="last-name-field"
                    name="lastName"
                    className='form-control'
                    value={state.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.lastName &&
                        !state.lastName?.trim() &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className='col-12'>
                <label
                    htmlFor="billing-address-field"
                    className='form-label'>
                    Street
                </label>
                <input
                    id="billing-address-field"
                    name="billingAddress"
                    className='form-control'
                    value={state.billingAddress}
                    onChange={handleChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.billingAddress &&
                        !state.billingAddress?.trim() &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className={`col-${state.country && state.country.name && state.country.states.length === 0 ? "12" : "6"}`}>
                <label className='form-label' htmlFor='country-field'>
                    Country / Region
                </label>
                <ComboBox
                    id="country-field"
                    name="country"
                    className='form-control-custom'
                    data={countries}
                    filterable={true}
                    onFilterChange={filterByName}
                    textField="name"
                    dataItemKey='name'
                    value={state.country}
                    onChange={handleCountryChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.country &&
                        !state.country.name &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className={`${state.country && state.country.name && state.country.states.length === 0 ? "visually-hidden" : "col-6"}`}>
                <label
                    htmlFor="state-region-field"
                    className='form-label'>
                    State/Province
                </label>
                <ComboBox
                    id="state-region-field"
                    name="state"
                    className='form-control-custom'
                    onFilterChange={filterByName}
                    filterable={true}
                    data={states}
                    value={state.state}
                    textField={"name"}
                    dataItemKey={"name"}
                    onChange={handleChange}
                    disabled={state.country.name === undefined}
                    onBlur={handleBlur} />
                    {
                        focusedFields.state &&
                        !state.state.name &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className='col-6'>
                <label
                    htmlFor="city-field"
                    className='form-label'>
                    City
                </label>
                <input
                    id="city-field"
                    name="city"
                    className='form-control'
                    value={state.city}
                    onChange={handleChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.city &&
                        !state.city?.trim() &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
            <div className='col-6'>
                <label
                    htmlFor="zip-code-field"
                    className='form-label'>
                    Zip Code
                </label>
                <input
                    id="zip-code-field"
                    name="zipCode"
                    className='form-control'
                    value={state.zipCode}
                    onChange={handleChange}
                    onBlur={handleBlur} />
                    {
                        focusedFields.zipCode &&
                        !state.zipCode?.trim() &&
                        <span className='text-danger'>
                            &nbsp;This field is required
                        </span>
                    }
            </div>
        </>
    )
}

export default BillingInformationForm;